import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, ArrowRight, ArrowLeft, Mail, CheckCircle2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import TechBackground from "@/components/TechBackground";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Self-serve "Forgot password" — sends a reset link to the user's email.
 *
 * Anti-enumeration: the backend always returns 200, regardless of whether
 * the email exists. We mirror that here by showing the same success state
 * either way — never tell the user "no account found".
 */
export default function ForgotPassword() {
    const [email, setEmail] = useState("");
    const [busy, setBusy] = useState(false);
    const [sent, setSent] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await axios.post(`${API}/auth/forgot-password`, { email });
            setSent(true);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            const msg = typeof detail === "string" ? detail : "Something went wrong. Try again.";
            // 429 → lockout. 422 → bad email format. Otherwise: still pretend success
            // to keep the anti-enumeration promise consistent.
            if (err?.response?.status === 429) {
                toast.error(msg);
            } else if (err?.response?.status === 422) {
                toast.error("Please enter a valid email address.");
            } else {
                setSent(true);
            }
        } finally {
            setBusy(false);
        }
    };

    return (
        <div data-testid="forgot-password-page" className="min-h-[calc(100vh-68px)] grid lg:grid-cols-2 bg-white">
            {/* Visual side */}
            <div className="relative hidden lg:block bg-[#0B1120] tr-grain overflow-hidden">
                <TechBackground variant="dark" density={1.0} />
                <div className="absolute -top-32 -right-20 w-[500px] h-[500px] tr-glow bg-cyan-500/30" />
                <div className="absolute bottom-0 -left-32 w-[420px] h-[420px] tr-glow bg-teal-500/25" />
                <div className="relative h-full flex flex-col justify-between p-12 text-white">
                    <Link to="/" className="font-display text-xl tracking-tight">
                        Toothrocket<span className="text-[10px] align-top ml-0.5 text-white/40">™</span>
                    </Link>
                    <div>
                        <div className="tr-overline text-cyan-400 mb-3">· Account recovery</div>
                        <h1 className="font-display font-light text-5xl tracking-tight leading-[1.05]">
                            Lost the key?<br />
                            <span className="italic text-cyan-300/90 font-extralight">We&apos;ll mint a new one.</span>
                        </h1>
                        <p className="mt-6 text-white/70 max-w-md text-[15px] leading-relaxed">
                            Reset links are single-use and expire in 24 hours. We never email passwords — only secure, scoped links.
                        </p>
                    </div>
                    <div className="font-mono-tr text-[11px] tracking-[0.2em] uppercase text-white/40">· Encrypted · TLS · Anti-enumeration</div>
                </div>
            </div>

            {/* Form side */}
            <div className="flex items-center justify-center p-6 lg:p-12">
                <div className="w-full max-w-md">
                    <Link to="/login" data-testid="forgot-back-to-login" className="inline-flex items-center gap-1.5 text-[13px] text-slate-500 hover:text-slate-900 mb-6">
                        <ArrowLeft size={14} /> Back to sign in
                    </Link>

                    {!sent ? (
                        <>
                            <div className="tr-overline mb-3">· Forgot password</div>
                            <h1 className="font-display font-light text-4xl tracking-tight leading-tight text-slate-900">Reset your password.</h1>
                            <p className="mt-2 text-slate-600 text-[14.5px]">Enter the email tied to your Toothrocket account. We&apos;ll send you a one-time link valid for 24 hours.</p>

                            <form onSubmit={submit} className="mt-10 space-y-5" data-testid="forgot-form">
                                <label className="block">
                                    <div className="font-mono-tr text-[10.5px] tracking-[0.22em] uppercase text-slate-500 mb-2">Email</div>
                                    <input
                                        data-testid="forgot-email"
                                        type="email"
                                        required
                                        autoFocus
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100 transition outline-none text-[14.5px]"
                                        placeholder="dentist@clinic.com"
                                    />
                                </label>
                                <button
                                    data-testid="forgot-submit"
                                    type="submit"
                                    disabled={busy || !email}
                                    className="w-full tr-btn justify-center !py-3.5 disabled:opacity-60"
                                >
                                    {busy
                                        ? (<><Loader2 size={16} className="animate-spin" /> Sending…</>)
                                        : (<>Send reset link <ArrowRight size={16} /></>)}
                                </button>
                            </form>
                        </>
                    ) : (
                        <div data-testid="forgot-success" className="text-center lg:text-left">
                            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-50 text-emerald-600 mb-5">
                                <CheckCircle2 size={26} />
                            </div>
                            <h1 className="font-display font-light text-4xl tracking-tight leading-tight text-slate-900">Check your inbox.</h1>
                            <p className="mt-3 text-slate-600 text-[14.5px] leading-relaxed">
                                If an account exists for <strong className="text-slate-900">{email}</strong>, we&apos;ve sent a reset link. It&apos;s valid for the next 24 hours and can be used once.
                            </p>
                            <div className="mt-6 rounded-lg border border-slate-200 bg-slate-50/60 p-4 text-[13px] text-slate-600 inline-flex items-start gap-3 max-w-md">
                                <Mail size={16} className="text-slate-400 mt-0.5 shrink-0" />
                                <span>Didn&apos;t get the email? Check your spam folder, or wait a couple of minutes — delivery is usually instant but can occasionally lag.</span>
                            </div>
                            <div className="mt-8">
                                <button
                                    data-testid="forgot-try-again"
                                    onClick={() => { setSent(false); setEmail(""); }}
                                    className="text-[13px] text-cyan-700 hover:text-cyan-900 underline-offset-2 hover:underline"
                                >
                                    Use a different email
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
